import React, { useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

export type CatalogViewState =
  "loaded" | "loading" | "error" | "empty" | "forbidden";

type ProductStatus = "Available" | "Planned";
type ProductMark = "gateway" | "identity";

interface ProductCatalogEntry {
  key: string;
  name: string;
  category: string;
  description: string;
  capabilities: string[];
  mark: ProductMark;
  glyph: string;
  status: ProductStatus;
  viewable: boolean;
}

const CATALOG_PRODUCTS: ProductCatalogEntry[] = [
  {
    key: "llm-gateway",
    name: "LLM Gateway",
    category: "AI infrastructure",
    description:
      "A managed, organization-scoped gateway for routing application requests to approved language-model backends.",
    capabilities: ["OpenAI-compatible API", "Usage visibility"],
    mark: "gateway",
    glyph: "\u2726",
    status: "Available",
    viewable: true,
  },
  {
    key: "customer-identity",
    name: "Customer Identity",
    category: "Application identity",
    description:
      "A managed identity platform for customer applications and organization identity boundaries.",
    capabilities: ["User directories", "OIDC and OAuth clients", "Federation"],
    mark: "identity",
    glyph: "\u25CE",
    status: "Planned",
    viewable: false,
  },
];

const BOUNDARY_INFO = [
  {
    title: "Organization-scoped",
    body: "Products are presented in the context of the selected organization.",
  },
  {
    title: "Separate from Projects",
    body: "Rusha products are not customer projects, stacks, or deployed applications.",
  },
  {
    title: "Read-only discovery",
    body: "This catalog does not activate products, issue credentials, or persist changes.",
  },
];

type StatusFilter = "all" | ProductStatus;
const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All products" },
  { value: "Available", label: "Available" },
  { value: "Planned", label: "Planned" },
];

export interface PlatformProductsPageProps {
  /** Optional deterministic state hook for route and state coverage. */
  viewState?: CatalogViewState;
}

const PlatformProductsPage: React.FC<PlatformProductsPageProps> = ({
  viewState = "loaded",
}) => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  if (!orgAlias) return null;

  const renderState = () => {
    if (viewState === "loading") return <CatalogSkeleton />;
    if (viewState === "error") {
      return (
        <Alert severity="error" role="alert">
          Platform Products could not be loaded. Refresh the page to try again.
        </Alert>
      );
    }
    if (viewState === "forbidden") {
      return (
        <ForbiddenState
          onBack={() => appNavigation.goToOrganizationDetails(orgAlias)}
        />
      );
    }
    if (viewState === "empty") return <EmptyState />;

    return (
      <CatalogContent
        orgAlias={orgAlias}
        statusFilter={statusFilter}
        onFilterChange={setStatusFilter}
        onView={(productKey) =>
          appNavigation.goToPlatformProductDetails(orgAlias, productKey)
        }
      />
    );
  };

  return (
    <Container sx={getContainerStyles(theme)}>
      <AppBreadcrumbs
        items={[
          { label: "Organizations", to: ORGANIZATIONS.LIST.path },
          { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
          { label: PLATFORM_PRODUCTS.CATALOG.title },
        ]}
      />
      {renderState()}
    </Container>
  );
};

const CatalogContent: React.FC<{
  orgAlias: string;
  statusFilter: StatusFilter;
  onFilterChange: (value: StatusFilter) => void;
  onView: (productKey: string) => void;
}> = ({ orgAlias, statusFilter, onFilterChange, onView }) => {
  const productStatuses = useMemo(
    () =>
      Object.fromEntries(
        CATALOG_PRODUCTS.map((product) => [product.key, product.status]),
      ),
    [],
  ) as Record<string, ProductStatus>;
  const visibleProducts = useMemo(
    () =>
      statusFilter === "all"
        ? CATALOG_PRODUCTS
        : CATALOG_PRODUCTS.filter((product) => product.status === statusFilter),
    [statusFilter],
  );

  return (
    <Stack spacing={3}>
      <CatalogHeader orgAlias={orgAlias} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Typography variant="body2" color="text.secondary">
          {visibleProducts.length}{" "}
          {visibleProducts.length === 1 ? "product" : "products"} in the catalog
        </Typography>
        <FormControl
          size="small"
          sx={{ minWidth: 220, width: { xs: "100%", sm: 220 } }}
        >
          <InputLabel id="product-status-filter-label">
            Filter products
          </InputLabel>
          <Select<StatusFilter>
            labelId="product-status-filter-label"
            id="product-status-filter"
            value={statusFilter}
            label="Filter products"
            onChange={(event) =>
              onFilterChange(event.target.value as StatusFilter)
            }
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {visibleProducts.length === 0 ? (
        <EmptyState />
      ) : (
        <Grid container spacing={2}>
          {visibleProducts.map((product) => (
            <Grid item xs={12} md={6} key={product.key}>
              <ProductCard
                product={product}
                status={productStatuses[product.key]}
                onView={
                  product.viewable ? () => onView(product.key) : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
      )}
      <BoundaryGrid />
    </Stack>
  );
};

const CatalogHeader: React.FC<{ orgAlias: string }> = ({ orgAlias }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", sm: "flex-start" }}
    spacing={3}
  >
    <Stack spacing={1} sx={{ maxWidth: 680 }}>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.08em",
          fontWeight: 700,
          color: "text.secondary",
        }}
      >
        Rusha Platform Products
      </Typography>
      <Typography variant="h4" component="h1" fontWeight={700}>
        Products for your organization
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, maxWidth: 620 }}
      >
        Discover Rusha-managed services available to <strong>{orgAlias}</strong>
        . Product details in this release are read-only.
      </Typography>
    </Stack>
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        py: 1,
        px: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        fontSize: 12,
        color: "text.secondary",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: "success.main",
        }}
      />
      Organization: <strong>{orgAlias}</strong>
    </Stack>
  </Stack>
);

const ProductCard: React.FC<{
  product: ProductCatalogEntry;
  status: ProductStatus;
  onView?: () => void;
}> = ({ product, status, onView }) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 268,
        display: "flex",
        flexDirection: "column",
        borderRadius: 1.5,
        transition:
          "border-color 150ms ease, transform 150ms ease, background-color 150ms ease",
        "&:hover": {
          borderColor: "divider",
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flexGrow: 1, "&:last-child": { pb: 2.5 } }}>
        <Stack spacing={2.25}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 0.5 }}
          >
            <ProductMark mark={product.mark} glyph={product.glyph} />
            <StatusChip status={status} />
          </Stack>
          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
              {product.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {product.category}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.55, minHeight: 58 }}
          >
            {product.description}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {product.capabilities.map((capability) => (
              <Chip
                key={capability}
                label={capability}
                size="small"
                sx={{
                  height: "auto",
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: "6px",
                  backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  "& .MuiChip-label": { padding: "2px 0", fontWeight: 500 },
                }}
              />
            ))}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: "space-between",
          px: 2.5,
          pb: 2.5,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          sx={{ maxWidth: 205, lineHeight: 1.4, color: "text.secondary" }}
        >
          <strong>Next step:</strong>{" "}
          {onView
            ? "Review read-only product details."
            : "Details are not available in this release."}
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={onView}
          disabled={!onView}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {onView ? "View product" : "Unavailable"}
        </Button>
      </CardActions>
    </Card>
  );
};

const ProductMark: React.FC<{ mark: ProductMark; glyph: string }> = ({
  mark,
  glyph,
}) => (
  <Box
    aria-hidden
    sx={{
      width: 44,
      height: 44,
      display: "grid",
      placeItems: "center",
      borderRadius: "12px",
      fontSize: 18,
      fontWeight: 700,
      color: mark === "identity" ? "#c4b5fd" : "#bfdbfe",
      backgroundColor:
        mark === "identity"
          ? "rgba(124, 58, 237, 0.18)"
          : "rgba(37, 99, 235, 0.2)",
      border: "1px solid",
      borderColor:
        mark === "identity"
          ? "rgba(167, 139, 250, 0.34)"
          : "rgba(96, 165, 250, 0.35)",
    }}
  >
    {glyph}
  </Box>
);

const StatusChip: React.FC<{ status: ProductStatus }> = ({ status }) => (
  <Chip
    label={status}
    color={status === "Available" ? "info" : "default"}
    variant="outlined"
    size="small"
  />
);

const BoundaryGrid: React.FC = () => (
  <Grid container spacing={1.25} sx={{ mt: 1 }}>
    {BOUNDARY_INFO.map((item) => (
      <Grid item xs={12} sm={4} key={item.title}>
        <Box
          sx={{
            p: 1.75,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: alpha("#000", 0.02),
          }}
        >
          <Typography
            component="strong"
            sx={{
              display: "block",
              mb: 0.5,
              fontSize: 12,
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", lineHeight: 1.45, display: "block" }}
          >
            {item.body}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

const CatalogSkeleton: React.FC = () => (
  <Stack spacing={3} role="status" aria-label="Loading Platform Products">
    <Stack spacing={1}>
      <Skeleton variant="text" width={160} height={16} />
      <Skeleton variant="text" width={280} height={32} />
      <Skeleton variant="text" width={520} height={20} />
    </Stack>
    <Grid container spacing={2}>
      {CATALOG_PRODUCTS.map((product) => (
        <Grid item xs={12} md={6} key={product.key}>
          <Skeleton variant="rounded" height={268} />
        </Grid>
      ))}
    </Grid>
  </Stack>
);

const EmptyState: React.FC = () => (
  <Box
    sx={{
      maxWidth: 480,
      mx: "auto",
      my: 6,
      p: 4,
      textAlign: "center",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Typography
      component="div"
      aria-hidden
      sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
    >
      ◊
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      No products are available
    </Typography>
    <Typography variant="body2" color="text.secondary">
      There are no products matching this filter for the selected organization.
    </Typography>
  </Box>
);

const ForbiddenState: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Box
    sx={{
      maxWidth: 520,
      mx: "auto",
      my: 6,
      p: 4,
      textAlign: "center",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Typography
      component="div"
      aria-hidden
      sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
    >
      !
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      Product catalog unavailable
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      You do not have permission to discover Rusha Platform Products for this
      organization.
    </Typography>
    <Button variant="outlined" onClick={onBack} sx={{ textTransform: "none" }}>
      Back to organization
    </Button>
  </Box>
);

export default PlatformProductsPage;
